
/**
 * Google Ads Service
 * Provides methods for tracking conversions and remarketing events for Google Ads
 */
class GoogleAdsService {
  constructor() {
    this.isInitialized = false;
    this.conversionId = process.env.REACT_APP_GOOGLE_ADS_CONVERSION_ID;
    this.remarketingId = process.env.REACT_APP_GOOGLE_ADS_REMARKETING_ID || this.conversionId;
  }

  /**
   * Initialize Google Ads tracking
   */
  init() {
    if (!this.conversionId || this.conversionId === 'AW-XXXXXXXXXX') {
      console.warn('Google Ads: Conversion ID not configured');
      return;
    }

    if (this.isInitialized) {
      console.log('Google Ads: Already initialized');
      return;
    }

    try {
      // Load gtag script if not already loaded
      if (!window.gtag) {
        // Create script tag for gtag
        const script = document.createElement('script');
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${this.conversionId}`;
        document.head.appendChild(script);

        // Initialize gtag function
        window.dataLayer = window.dataLayer || [];
        window.gtag = function() {
          window.dataLayer.push(arguments);
        };
      }

      // Configure Google Ads
      window.gtag('js', new Date());
      window.gtag('config', this.conversionId);
      
      this.isInitialized = true;
      console.log('Google Ads: Initialized successfully', this.conversionId);
    } catch (error) {
      console.error('Google Ads: Initialization failed', error);
    }
  }

  /**
   * Track conversion events
   * @param {string} conversionLabel - The conversion label from Google Ads
   * @param {Object} conversionData - Additional conversion data
   */
  trackConversion(conversionLabel, conversionData = {}) {
    if (!this.isInitialized || !window.gtag) {
      console.warn('Google Ads: Not initialized');
      return;
    }

    if (!conversionLabel) {
      console.warn('Google Ads: Conversion label is required');
      return;
    }

    try {
      const eventData = {
        send_to: `${this.conversionId}/${conversionLabel}`,
        value: conversionData.value || 0,
        currency: conversionData.currency || 'USD',
        transaction_id: conversionData.transaction_id || '',
        ...conversionData
      };

      window.gtag('event', 'conversion', eventData);
      console.log('Google Ads: Conversion tracked', { conversionLabel, eventData });
    } catch (error) {
      console.error('Google Ads: Conversion tracking failed', error);
    }
  }

  /**
   * Track remarketing events for audience building
   * @param {string} eventName - Custom event name for remarketing
   * @param {Object} parameters - Event parameters for audience segmentation
   */
  trackRemarketingEvent(eventName, parameters = {}) {
    if (!this.isInitialized || !window.gtag) {
      console.warn('Google Ads: Not initialized');
      return;
    }

    try {
      const eventData = {
        custom_parameters: {
          event_category: 'remarketing',
          event_label: eventName,
          ...parameters
        }
      };

      window.gtag('event', eventName, eventData);
      console.log('Google Ads: Remarketing event tracked', { eventName, eventData });
    } catch (error) {
      console.error('Google Ads: Remarketing tracking failed', error);
    }
  }

  /**
   * Track user registration for conversion and remarketing
   * @param {Object} data - Registration data
   */
  trackRegistration(data = {}) {
    // Track as conversion
    this.trackConversion('registration', {
      value: data.value || 1,
      currency: data.currency || 'USD',
      user_data: data.user_data || {}
    });

    // Track for remarketing audiences
    this.trackRemarketingEvent('sign_up', {
      method: data.method || 'email',
      source: data.source || 'website',
      user_type: 'registered_user'
    });
  }

  /**
   * Track user login for remarketing
   * @param {Object} data - Login data
   */
  trackLogin(data = {}) {
    this.trackRemarketingEvent('login', {
      method: data.method || 'email',
      user_type: 'returning_user',
      login_frequency: data.login_frequency || 'unknown'
    });
  }

  /**
   * Track website analysis for lead generation conversion
   * @param {Object} data - Analysis data
   */
  trackWebsiteAnalysis(data = {}) {
    // Track as lead generation conversion
    this.trackConversion('website_analysis', {
      value: data.value || 0,
      currency: data.currency || 'USD'
    });

    // Track for remarketing
    this.trackRemarketingEvent('website_analysis', {
      analysis_type: data.analysis_type || 'pagespeed',
      url_analyzed: data.url ? 'yes' : 'no',
      source: data.source || 'public_analyzer',
      user_intent: 'analysis_seeker'
    });
  }

  /**
   * Track website addition for conversion and remarketing
   * @param {Object} data - Website data
   */
  trackWebsiteAdded(data = {}) {
    // Track as conversion
    this.trackConversion('website_added', {
      value: data.value || 5, // Assign value to website addition
      currency: data.currency || 'USD'
    });

    // Track for remarketing
    this.trackRemarketingEvent('add_website', {
      platform: data.platform || 'unknown',
      website_count: data.website_count || 1,
      user_type: 'active_user',
      engagement_level: 'high'
    });
  }

  /**
   * Track support ticket creation
   * @param {Object} data - Ticket data
   */
  trackSupportTicket(data = {}) {
    // Track for remarketing (support users need special attention)
    this.trackRemarketingEvent('support_request', {
      ticket_type: data.type || 'general',
      priority: data.priority || 'normal',
      user_type: 'support_seeker',
      engagement_level: 'high'
    });
  }

  /**
   * Track purchases for conversion and remarketing
   * @param {number} value - Purchase value
   * @param {string} currency - Currency code
   * @param {Object} data - Additional purchase data
   */
  trackPurchase(value, currency = 'USD', data = {}) {
    // Track as purchase conversion
    this.trackConversion('purchase', {
      value: parseFloat(value) || 0,
      currency: currency,
      transaction_id: data.transaction_id || Date.now().toString(),
      items: data.items || []
    });

    // Track for remarketing
    this.trackRemarketingEvent('purchase', {
      purchase_value: parseFloat(value) || 0,
      currency: currency,
      product_type: data.product_type || 'subscription',
      user_type: 'paying_customer',
      customer_lifetime_value: data.customer_lifetime_value || 'unknown'
    });
  }

  /**
   * Track subscription events
   * @param {Object} data - Subscription data
   */
  trackSubscription(data = {}) {
    const subscriptionValue = data.value || 0;
    
    // Track as subscription conversion
    this.trackConversion('subscription', {
      value: subscriptionValue,
      currency: data.currency || 'USD',
      subscription_tier: data.plan_name || 'basic'
    });

    // Track for remarketing
    this.trackRemarketingEvent('subscribe', {
      plan_name: data.plan_name || 'basic',
      plan_value: subscriptionValue,
      subscription_type: data.billing_cycle || 'monthly',
      user_type: 'subscriber',
      conversion_stage: 'converted'
    });
  }

  /**
   * Track page views for remarketing audiences
   * @param {string} page_location - Page URL
   * @param {string} page_title - Page title
   */
  trackPageView(page_location, page_title) {
    if (!this.isInitialized || !window.gtag) {
      console.warn('Google Ads: Not initialized');
      return;
    }

    try {
      window.gtag('config', this.conversionId, {
        page_location: page_location,
        page_title: page_title
      });
      console.log('Google Ads: Page view tracked', { page_location, page_title });
    } catch (error) {
      console.error('Google Ads: Page view tracking failed', error);
    }
  }

  /**
   * Track custom audience events
   * @param {string} audienceName - Name of the custom audience
   * @param {Object} parameters - Parameters for audience qualification
   */
  trackCustomAudience(audienceName, parameters = {}) {
    this.trackRemarketingEvent(`audience_${audienceName}`, {
      audience_type: audienceName,
      qualification_time: new Date().toISOString(),
      ...parameters
    });
  }

  /**
   * Enhanced ecommerce tracking for dynamic remarketing
   * @param {string} eventType - Type of ecommerce event
   * @param {Object} eventData - Event data
   */
  trackEcommerce(eventType, eventData = {}) {
    if (!this.isInitialized || !window.gtag) {
      console.warn('Google Ads: Not initialized');
      return;
    }

    try {
      const enhancedEventData = {
        send_to: this.conversionId,
        ...eventData
      };

      window.gtag('event', eventType, enhancedEventData);
      console.log('Google Ads: Ecommerce event tracked', { eventType, enhancedEventData });
    } catch (error) {
      console.error('Google Ads: Ecommerce tracking failed', error);
    }
  }
}

// Create and export a singleton instance
const googleAdsService = new GoogleAdsService();
export default googleAdsService;
